"use client";

import { useSyncExternalStore } from "react";

// A minute clock for statically rendered (ISR) pages whose verdict depends on the time, such as
// the sailing page's score freshness (lib/conditions/sailing-freshness.ts). The server renders
// with the time it rendered at (`serverNowMs`, also used during hydration so the HTML matches);
// right after hydration the browser's own clock takes over, and it ticks once a minute, so a page
// rebuilt hours ago never keeps the verdict of its build.

export const MINUTE_CLOCK_TICK_MS = 60_000;

const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

/** Subscribes to the shared minute tick (one interval for every subscriber). */
export function subscribeMinuteClock(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    timer = setInterval(() => {
      for (const callback of listeners) callback();
    }, MINUTE_CLOCK_TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** The browser's time, floored to the minute so the snapshot is stable between ticks. */
export function minuteClockSnapshot(nowMs: number = Date.now()): number {
  return Math.floor(nowMs / MINUTE_CLOCK_TICK_MS) * MINUTE_CLOCK_TICK_MS;
}

/**
 * The time to judge by: the server's render time on the server and during hydration, then the
 * browser's clock, never earlier than the server's (a slow device clock cannot revive a verdict the
 * server already judged).
 */
export function useMinuteClock(serverNowMs: number): number {
  return useSyncExternalStore(
    subscribeMinuteClock,
    () => Math.max(serverNowMs, minuteClockSnapshot()),
    () => serverNowMs,
  );
}

function subscribeNever(): () => void {
  return () => undefined;
}

/**
 * False on the server and during hydration, true after it. A statically rendered page uses it to
 * keep claims about "today" out of its HTML (which can be served hours after it was built) and
 * make them only once the browser's clock has judged them, without a hydration mismatch.
 */
export function useHydrated(): boolean {
  return useSyncExternalStore(subscribeNever, () => true, () => false);
}

/**
 * The browser's minute clock once hydrated, or null on the server and during hydration. For a
 * statically rendered list already judged on the server at its render (the board table,
 * lib/conditions/board-freshness.ts): it renders the server's verdicts first, so the HTML matches,
 * then judges again on the browser's clock. Such a judgement only ever withdraws a claim, so a slow
 * device clock cannot revive one.
 */
export function useBrowserMinuteClock(): number | null {
  return useSyncExternalStore(subscribeMinuteClock, () => minuteClockSnapshot(), () => null);
}
